import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useApi } from '@lib/api';
import { useUser } from '@/store/user';
import { signalrListen } from '@lib/signalr';
import {
  AgentApprovalState,
  AgentMessageRole,
  AgentThreadStatus,
  type AgentMessageDto,
  type AgentThreadDto,
  type AgentThreadSummaryDto,
} from '@/ApiClient';

// What the backend pushes over SignalR while a turn runs (AgentThreadEventDto in Api.Domain).
// Not in the generated client, which only knows types an endpoint returns.
export interface AgentThreadEvent {
  threadId: number;
  type: 'delta' | 'message' | 'status' | 'thinking';
  eventId?: string;
  text?: string;
  message?: AgentMessageDto;
  thread?: AgentThreadSummaryDto;
}

// The agent chat: the user's threads and the one open in the panel. Everything the agent does
// while a turn runs arrives as `AgentThreadEvent` and is applied here — text as it is written,
// transcript rows as they complete, status as it changes — so the panel only renders state.
export const useAgentChat = defineStore('agent-chat', () => {
  const api = useApi();

  // Null until asked. The tab is hidden while the server has no agent configured.
  const enabled = ref<boolean | null>(null);

  // Archived threads are loaded alongside the rest: archiving hides a thread from the list but
  // keeps its transcript, so the two are one list with a flag rather than two fetches.
  const threads = ref<AgentThreadSummaryDto[]>([]);
  const threadsLoaded = ref(false);
  const threadsLoading = ref(false);

  const activeThreads = computed(() => threads.value.filter((t) => !t.isArchived));
  const archivedThreads = computed(() => threads.value.filter((t) => t.isArchived));

  const activeThread = ref<AgentThreadDto | null>(null);
  const activeLoading = ref(false);

  // What the user has typed but not sent, per thread. The panel unmounts when it closes, so a
  // draft held in the composer is a draft lost on a stray click outside — and the attachments
  // with it, which are already uploaded by then.
  const drafts_ = ref<Record<number, { text: string; attachments: { key: number; name: string; fileId: number }[] }>>(
    {}
  );
  const draftFor = (threadId: number) => drafts_.value[threadId] ?? { text: '', attachments: [] };
  const setDraft = (
    threadId: number,
    draft: { text: string; attachments: { key: number; name: string; fileId: number }[] }
  ) => {
    if (!draft.text.trim() && !draft.attachments.length) delete drafts_.value[threadId];
    else drafts_.value[threadId] = draft;
  };

  // Assistant text still being written, keyed by the platform event that will become the
  // message. Replaced by the message row when it arrives, so a draft never outlives its turn.
  const drafts = ref<Record<string, string>>({});
  const thinking = ref(false);

  const activeStatus = computed(() => activeThread.value?.summary.status ?? null);
  const isBusy = computed(
    () =>
      activeStatus.value === AgentThreadStatus.Running || activeStatus.value === AgentThreadStatus.WaitingForApproval
  );
  const pendingApprovals = computed<AgentMessageDto[]>(
    () => activeThread.value?.messages.filter((m) => m.approvalState === AgentApprovalState.Pending) ?? []
  );

  const loadStatus = async () => {
    if (enabled.value !== null) return enabled.value;
    enabled.value = (await api.agentChat_Status()).enabled;
    return enabled.value;
  };

  const loadThreads = async () => {
    threadsLoading.value = true;
    try {
      threads.value = await api.agentChat_ListThreads(useUser().brandId, true);
      threadsLoaded.value = true;
    } finally {
      threadsLoading.value = false;
    }
  };

  // A thread locked to another brand is only seen with that brand open. Every tab hears every
  // thread's events, and one locked elsewhere from another tab has to leave this one — the list,
  // and the panel if it is the thread being shown.
  const listedHere = (summary: AgentThreadSummaryDto) =>
    summary.lockedBrandId == null || summary.lockedBrandId === useUser().brandId;

  const upsertSummary = (summary: AgentThreadSummaryDto) => {
    const index = threads.value.findIndex((t) => t.id === summary.id);
    if (!listedHere(summary)) {
      if (index !== -1) threads.value.splice(index, 1);
      if (activeThread.value?.summary.id === summary.id) activeThread.value = null;
      return;
    }
    if (index === -1) threads.value.unshift(summary);
    else threads.value[index] = summary;
    threads.value.sort((a, b) => (a.lastActivityAt < b.lastActivityAt ? 1 : -1));
  };

  /// The thread the panel is showing, from a response that carries the whole of it.
  ///
  /// Rows already on screen are kept when it is the same thread. The response was projected on
  /// the server before it travelled, while the turn's job was pushing rows over SignalR; a row
  /// that arrived in that window is not in the response and is never sent again, so replacing
  /// outright drops a finished answer until the thread is next opened.
  const setActive = (thread: AgentThreadDto | null) => {
    const showing = activeThread.value;
    if (thread && showing?.summary.id === thread.summary.id) {
      const arrived = new Set(thread.messages.map((m) => m.id));
      const missing = showing.messages.filter((m) => !arrived.has(m.id));
      if (missing.length) thread.messages = [...thread.messages, ...missing].sort((a, b) => a.seq - b.seq);
    }

    activeThread.value = thread;
    drafts.value = {};
    thinking.value = false;
    if (thread) upsertSummary(thread.summary);
  };

  const openThread = async (threadId: number) => {
    activeLoading.value = true;
    try {
      setActive(await api.agentChat_GetThread(threadId, useUser().brandId));
    } finally {
      activeLoading.value = false;
    }
  };

  const newThread = async () => {
    setActive(await api.agentChat_CreateThread({ brandId: useUser().brandId, title: undefined }));
  };

  const closeThread = () => setActive(null);

  const send = async (text: string, attachmentFileIds: number[], context?: { url: string; label: string }) => {
    if (!activeThread.value) await newThread();
    const thread = activeThread.value!;
    setActive(
      await api.agentChat_SendMessage(thread.summary.id, {
        brandId: useUser().brandId,
        text,
        attachmentFileIds,
        contextUrl: context?.url,
        contextLabel: context?.label,
      })
    );
  };

  const approve = async (messageId: number, allow: boolean) => {
    if (!activeThread.value) return;
    setActive(await api.agentChat_Approve(activeThread.value.summary.id, useUser().brandId, { messageId, allow }));
  };

  const cancel = async () => {
    if (!activeThread.value) return;
    setActive(await api.agentChat_Cancel(activeThread.value.summary.id, useUser().brandId));
  };

  const update = async (
    changes: Partial<Pick<AgentThreadSummaryDto, 'title' | 'autoApproveWrites' | 'lockedBrandId'>>
  ) => {
    if (!activeThread.value) return;
    const { title, autoApproveWrites, lockedBrandId } = { ...activeThread.value.summary, ...changes };
    const summary = await api.agentChat_UpdateThread(activeThread.value.summary.id, useUser().brandId, {
      title,
      autoApproveWrites,
      lockedBrandId,
    });
    activeThread.value = { ...activeThread.value, summary };
    upsertSummary(summary);
  };

  // The thread moves to the archived section rather than out of the list, and one that is open
  // stays open — it is still readable, only closed to new messages.
  /// The thread to show when the tab is opened with none chosen: the one worked on most recently.
  const openLatest = async () => {
    if (activeThread.value) return;
    const latest = activeThreads.value[0];
    if (latest) await openThread(latest.id);
  };

  const archive = async (threadId: number) => {
    await api.agentChat_ArchiveThread(threadId, useUser().brandId);
    const summary = threads.value.find((t) => t.id === threadId);
    if (summary) summary.isArchived = true;
    if (activeThread.value?.summary.id === threadId) activeThread.value.summary.isArchived = true;
  };

  const onEvent = (e: AgentThreadEvent) => {
    if (e.type === 'status' && e.thread) upsertSummary(e.thread);

    const active = activeThread.value;
    if (!active || active.summary.id !== e.threadId) return;

    switch (e.type) {
      case 'delta':
        if (e.eventId) drafts.value[e.eventId] = (drafts.value[e.eventId] ?? '') + (e.text ?? '');
        thinking.value = false;
        break;
      case 'thinking':
        thinking.value = true;
        break;
      case 'message': {
        const message = e.message!;
        // The row the agent just finished writing replaces the draft it was streamed as. The
        // draft is keyed by the turn's own id, which the row does not carry, so what identifies
        // it is that it is assistant text arriving at all.
        if (message.role === AgentMessageRole.Assistant) drafts.value = {};
        const index = active.messages.findIndex((m) => m.id === message.id);
        if (index === -1) active.messages.push(message);
        else active.messages[index] = message;
        active.messages.sort((a, b) => a.seq - b.seq);
        thinking.value = false;
        break;
      }
      case 'status':
        if (e.thread) active.summary = e.thread;
        if (e.thread?.status !== AgentThreadStatus.Running) {
          drafts.value = {};
          thinking.value = false;
        }
        break;
    }
  };

  const listen = () => {
    signalrListen('AgentThreadEvent', onEvent, { listenForever: true });
  };

  return {
    enabled,
    threads,
    activeThreads,
    archivedThreads,
    threadsLoaded,
    threadsLoading,
    activeThread,
    activeLoading,
    activeStatus,
    isBusy,
    pendingApprovals,
    drafts,
    thinking,
    loadStatus,
    loadThreads,
    openThread,
    openLatest,
    draftFor,
    setDraft,
    newThread,
    closeThread,
    send,
    approve,
    cancel,
    update,
    archive,
    onEvent,
    listen,
  };
});
